import Select, { SelectLabelContext } from "./ResponsiveSelect";
import Menu, { useMobileMenu } from "./ResponsiveMenu";
import FableTooltip from "./FableTooltip";
import FableTokenAmount from "./FableTokenAmount";
import React from "react";
import { FORM_CONTROL, circularControlVisualSx, dropdownInputVisualSx, dropdownVisualBorderSx, formPickerSurfaceSx, outlinedControlStyles, textInputRootStyles, textInputContentStyles } from "./formControlStyles";
import { visuallyHidden } from "@mui/utils";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  IconButton,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import GenerationProgress from "./GenerationProgress";
import HorizontalOverflowScroller from "./HorizontalOverflowScroller";
import DropdownScrollPaper from "./DropdownScrollPaper";
import AnimatedShimmerText from "./AnimatedShimmerText";
import { actionButtonSizingSx, compactButtonSizingSx } from "./buttonSizing";
import { productSectionTitleSx, productSupportingTextSx } from "./productTypography";

export type DetailOption = {
  value: string;
  label?: string;
  image?: string;
  defaultLabel?: boolean;
  locked?: boolean;
  unlockLabel?: string;
  audioFile?: string | null;
  subtitle?: string;
  statusLabel?: string;
  progress?: number;
  isSection?: boolean;
  isAction?: boolean;
  disabled?: boolean;
};

export type ToggleOption = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
  highlight?: boolean;
  gradientType?: keyof typeof TOGGLE_GRADIENTS;
  rightImage?: string;
  rightText?: string;
};

export const TOGGLE_GRADIENTS = {
  primary: "linear-gradient(115deg, #D8A363 0%, #E8A54C 50%, #FF9F32 100%)",
  orange: "linear-gradient(115deg, #F5A623 0%, #F5A623 100%)",
  "orange-royal-purple": "linear-gradient(115deg, #FF9700 0%, #6D28D9 100%)",
  "subscription-purple": "linear-gradient(115deg, #9B7ED9 0%, #6D28D9 50%, #7C3AED 100%)",
  "blue-purple": "linear-gradient(115deg, #4285F4 0%, #8A2BE2 100%)",
  "teal-blue": "linear-gradient(115deg, #00C9A7 0%, #1E90FF 100%)",
  gold: "linear-gradient(115deg, #D8A363 0%, #E8A54C 50%, #FF9F32 100%)",
  "orange-purple": "linear-gradient(115deg, #FFA500 0%, #8A2BE2 100%)",
} as const;

export const SELECTION_SURFACE = FORM_CONTROL.surface;
export const SELECTION_SURFACE_HOVER = FORM_CONTROL.hoverSurface;

export type ChapterMenuOption = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  endAdornment?: React.ReactNode;
  accent?: boolean;
  icon?: React.ReactNode;
  image?: string;
  selected?: boolean;
  showChevron?: boolean;
  sectionLabelBefore?: React.ReactNode;
  shimmerText?: boolean;
  progress?: number;
};

export const storyActionSheetPaperSx = {
  mt: 1,
  width: "min(350px, calc(100vw - 32px))",
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "min(760px, calc(100vh - 32px))",
  p: 0,
  bgcolor: "#101219",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: { xs: "24px", sm: "18px" },
  boxShadow: "0 24px 72px rgba(0,0,0,0.56)",
  overflowY: "auto",
} as const;

export const storyActionSheetItemSx = {
  minHeight: { xs: 54, sm: 44 },
  mx: { xs: 2, sm: 1.25 },
  my: 0.5,
  px: { xs: 1.5, sm: 1.1 },
  py: { xs: 1, sm: 0.65 },
  gap: { xs: 1.5, sm: 1.1 },
  color: "#E0E0E0",
  bgcolor: "#1B1E28",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "12px",
  fontWeight: 700,
  fontSize: 14,
  "&:hover": {
    bgcolor: "#20232E",
    borderColor: "rgba(255,255,255,0.20)",
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.48)",
    bgcolor: "#1B1E28",
    borderColor: "rgba(255,255,255,0.08)",
    opacity: 1,
  },
} as const;

export const storyActionSheetIconSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: { xs: 36, sm: 32 },
  minWidth: { xs: 36, sm: 32 },
  height: { xs: 36, sm: 32 },
  flexShrink: 0,
  borderRadius: "50%",
  bgcolor: "#252A36",
  color: "inherit",
  "& .MuiSvgIcon-root": { fontSize: 20 },
} as const;

export const storyActionSheetChevronSx = {
  ml: 0.25,
  flexShrink: 0,
  color: "#B9BAC5",
} as const;

export const storyOverflowMenuPaperSx = storyActionSheetPaperSx;
/** Equal leading and vertical padding for selection rows with a visual. */
export const selectionOptionPaddingSx = {
  px: { xs: 1, sm: 0.625 },
  py: { xs: 1, sm: 0.625 },
} as const;

export const storyOverflowMenuItemSx = {
  ...storyActionSheetItemSx,
  ...selectionOptionPaddingSx,
} as const;

export const inputSx = {
  "& .MuiInputBase-root": textInputRootStyles,
  "& .MuiOutlinedInput-input": textInputContentStyles,
  "& textarea.MuiOutlinedInput-input": { padding: 0 },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.62)" },
  "& .MuiInputLabel-root.Mui-focused": { color: FORM_CONTROL.focus },
};

export const formValueTextSx = {
  color: "#76A9E7",
  fontSize: { xs: "1.05rem", md: "1.08rem" },
  fontWeight: 850,
  lineHeight: 1.35,
} as const;

export const nameInputSx = inputSx;

/** Form dialogs need field labels, rather than page-section headings. */
export const modalFormLabelSx = {
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.4,
  color: "#E0E0E0",
} as const;

export const modalFormDescriptionSx = {
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.45,
  color: "#B9BAC5",
} as const;

export const modalNameInputSx = inputSx;

export const descriptionInputSx = inputSx;
export const boxedTextAreaSx = inputSx;

export const selectSx = {
  ...outlinedControlStyles,
  fontWeight: 800,
  textAlign: "left",
  // Inset the icon's full slot, keeping the arrow centered within that space.
  "& .MuiSelect-icon": { color: "#fff", right: FORM_CONTROL.dropdownInset },
  "& .MuiSelect-select": {
    color: "inherit",
    WebkitTextFillColor: "inherit",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minHeight: "24px !important",
    padding: `${FORM_CONTROL.dropdownInset}px 40px ${FORM_CONTROL.dropdownInset}px ${FORM_CONTROL.dropdownInset}px !important`,
  },
  "&.Mui-disabled .MuiSelect-select": {
    color: "rgba(255,255,255,0.42)",
    WebkitTextFillColor: "rgba(255,255,255,0.42)",
  },
};

export const menuPaperSx = {
  ...storyActionSheetPaperSx,
  width: "min(350px, calc(100vw - 32px))",
  maxHeight: 310,
  p: 0,
  "& .MuiList-root": {
    py: { xs: 1, sm: 0.75 },
  },
  "& .MuiMenuItem-root": {
    ...storyActionSheetItemSx,
    width: "auto",
  },
  "& .MuiMenuItem-root.Mui-selected": {
    color: "#fff",
    bgcolor: SELECTION_SURFACE,
    borderWidth: 2,
    borderColor: "#76A9E7",
  },
  "& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root:hover": {
    bgcolor: "#20232E",
    borderColor: "rgba(255,255,255,0.20)",
  },
  "& .MuiMenuItem-root.Mui-selected:hover": {
    bgcolor: SELECTION_SURFACE_HOVER,
    borderColor: "#76A9E7",
  },
  "&::-webkit-scrollbar": {
    width: 7,
  },
  "&::-webkit-scrollbar-track": {
    bgcolor: "rgba(255,255,255,0.04)",
  },
  "&::-webkit-scrollbar-thumb": {
    bgcolor: "rgba(255,255,255,0.24)",
    borderRadius: 999,
  },
};

export const menuProps = {
  variant: "menu" as const,
  slots: { paper: DropdownScrollPaper },
  PaperProps: { sx: menuPaperSx },
  MenuListProps: {
    disablePadding: true,
  },
};

export const dropdownSectionLabelSx = {
  mx: { xs: 2, sm: 1.25 },
  mt: { xs: 1.25, sm: 1 },
  mb: { xs: 0.5, sm: 0.35 },
  px: { xs: 1.5, sm: 1.1 },
  py: 0,
  minHeight: 28,
  lineHeight: "28px",
  color: "rgba(255,255,255,0.58)",
  bgcolor: "transparent",
  fontWeight: 900,
  fontSize: { xs: 14, sm: 13.5 },
} as const;

export const chapterMenuButtonSx = {
  ...actionButtonSizingSx,
  ...formPickerSurfaceSx,
  textTransform: "none",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  boxShadow: "none",
  justifyContent: "center",
  "& .MuiButton-endIcon": {
    ml: 0.9,
    mr: 0,
    "& svg": {
      fontSize: 18,
    },
  },
  "& .MuiButton-startIcon": {
    ml: 0,
    mr: 1.15,
    "& svg": {
      fontSize: 18,
    },
  },
};

// Form pickers share the same contained outline as text fields and selects.
export const chapterFormMenuButtonSx = {
  ...formPickerSurfaceSx,
  width: "100%",
  minHeight: FORM_CONTROL.minHeight,
  padding: `${FORM_CONTROL.dropdownInset - 2}px`,
  justifyContent: "flex-start",
  "& [data-form-picker-visual]": dropdownInputVisualSx,
  "& .MuiButton-startIcon": { display: "none" },
  "& .MuiButton-endIcon": {
    ml: "auto",
    mr: 0,
    width: 24,
    height: 24,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    "& svg": { fontSize: 20 },
  },
} as const;

export const ChapterMenuButton = ({
  label,
  ariaLabel,
  options,
  onChange,
  disabled,
  disableRipple = false,
  buttonSx,
  sheetTitle,
  sheetSubtitle,
  selectedValue,
  showSelectionState = false,
  showCloseAction = false,
  showSheetHandle = false,
  matchTriggerWidth = false,
  disableScrollLock = false,
}: {
  label: React.ReactNode;
  ariaLabel?: string;
  options: ChapterMenuOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  disableRipple?: boolean;
  buttonSx?: object;
  sheetTitle?: React.ReactNode;
  sheetSubtitle?: React.ReactNode;
  selectedValue?: string;
  showSelectionState?: boolean;
  showCloseAction?: boolean;
  showSheetHandle?: boolean;
  matchTriggerWidth?: boolean;
  disableScrollLock?: boolean;
}) => {
  const mobileSheet = useMobileMenu();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Button
        aria-label={ariaLabel}
        disabled={disabled}
        disableFocusRipple={disableRipple}
        disableRipple={disableRipple}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<MenuBookIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{ ...chapterMenuButtonSx, ...buttonSx }}
      >
        <Box
          component="span"
          sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {label}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock={disableScrollLock}
        PaperProps={{
          sx: {
            ...storyActionSheetPaperSx,
            width:
              matchTriggerWidth && anchorEl
                ? anchorEl.getBoundingClientRect().width
                : "min(380px, calc(100vw - 32px))",
            maxWidth: "calc(100vw - 32px)",
            py: { xs: 1, sm: 0.75 },
          },
        }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        {showSheetHandle && !mobileSheet && (
          <Box
            aria-hidden="true"
            sx={{
              width: 44,
              height: 4,
              mx: "auto",
              mt: 1,
              mb: 0.25,
              borderRadius: 99,
              bgcolor: "rgba(255,255,255,0.76)",
            }}
          />
        )}
        {sheetTitle && (
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ px: { xs: 2.25, sm: 1.6 }, pt: 1.25, pb: 0.9 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: { xs: 22, sm: 20 },
                  fontWeight: 850,
                  lineHeight: 1.2,
                }}
              >
                {sheetTitle}
              </Typography>
              {sheetSubtitle && (
                <Typography
                  sx={{
                    mt: 0.35,
                    color: "rgba(255,255,255,0.64)",
                    fontSize: { xs: 16, sm: 14.5 },
                    fontWeight: 650,
                    lineHeight: 1.3,
                  }}
                >
                  {sheetSubtitle}
                </Typography>
              )}
            </Box>
            <IconButton
              aria-label="Close"
              onClick={handleClose}
              sx={{
                mt: -0.25,
                width: { xs: 42, sm: 36 },
                height: { xs: 42, sm: 36 },
                flexShrink: 0,
                color: "#E0E0E0",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: "11px",
                bgcolor: "#1B1E28",
                "&:hover": { bgcolor: "#242834" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        )}
        {options.flatMap((option) => {
          const isSelected = option.selected ?? selectedValue === option.value;
          const menuItem = (
              <MenuItem
                key={option.value}
                disabled={option.disabled}
                onClick={() => {
                  if (option.disabled) return;
                  handleClose();
                  if (option.onClick) {
                    option.onClick();
                    return;
                  }
                  onChange(option.value);
                }}
                sx={{
                  ...storyActionSheetItemSx,
                  ...(showSelectionState && isSelected
                    ? {
                        color: "#fff",
                        borderWidth: 2,
                        borderColor: "#76A9E7",
                        bgcolor: SELECTION_SURFACE,
                      }
                    : {}),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&.Mui-disabled": {
                    color: "rgba(255,255,255,0.38)",
                    opacity: 1,
                  },
                  "&.Mui-focusVisible": {
                    bgcolor: showSelectionState && isSelected
                      ? SELECTION_SURFACE
                      : "#1B1E28",
                  },
                }}
              >
                <Stack
                  component="span"
                  direction="row"
                  alignItems="center"
                  spacing={{ xs: 1.5, sm: 1.1 }}
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  {(option.icon || option.image) && (
                    <Box
                      component="span"
                      sx={{
                        ...storyActionSheetIconSx,
                        ...(option.image
                          ? { p: 0, overflow: "hidden", bgcolor: "transparent" }
                          : {}),
                        ...(option.accent
                          ? { bgcolor: "#D8A363", color: "#101219" }
                          : {}),
                        ...(showSelectionState && isSelected && !option.image
                          ? { color: "#76A9E7" }
                          : {}),
                      }}
                    >
                      {option.image ? (
                        <Avatar
                          src={option.image}
                          alt=""
                          sx={{ width: "100%", height: "100%" }}
                        />
                      ) : option.icon}
                    </Box>
                  )}
                  <Stack
                    component="span"
                    spacing={0.65}
                    sx={{ minWidth: 0, flex: 1 }}
                  >
                    <Box
                      component="span"
                      sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {option.shimmerText && typeof option.label === "string" ? (
                        <AnimatedShimmerText
                          text={option.label}
                          noWrap
                          sx={{
                            fontSize: "inherit",
                            fontWeight: "inherit",
                            lineHeight: "inherit",
                          }}
                        />
                      ) : option.label}
                    </Box>
                    {typeof option.progress === "number" && (
                      <LinearProgress
                        aria-label={
                          option.progress > 1
                            ? `${option.label} ${Math.round(option.progress)}% complete`
                            : `${option.label} in progress`
                        }
                        variant={option.progress > 1 ? "determinate" : "indeterminate"}
                        value={option.progress > 1 ? Math.max(0, Math.min(100, option.progress)) : undefined}
                        sx={{
                          width: "100%",
                          height: 6,
                          borderRadius: "3px",
                          bgcolor: "rgba(255,255,255,0.16)",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: "3px",
                            bgcolor: "#76A9E7",
                          },
                        }}
                      />
                    )}
                  </Stack>
                </Stack>
                <Stack
                  component="span"
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ flexShrink: 0, color: "#fff" }}
                >
                  {option.endAdornment}
                  {option.showChevron ? (
                    <KeyboardArrowRightIcon sx={storyActionSheetChevronSx} />
                  ) : showSelectionState ? (
                    <OptionSelectionIndicator selected={isSelected} />
                  ) : null}
                </Stack>
              </MenuItem>
          );
          if (!option.sectionLabelBefore) return [menuItem];
          return [
            <Typography
              key={`${option.value}-section`}
              sx={{
                px: { xs: 2.25, sm: 1.6 },
                pt: 1.15,
                pb: 0.35,
                color: "rgba(255,255,255,0.62)",
                fontSize: { xs: 16, sm: 14 },
                fontWeight: 750,
              }}
            >
              {option.sectionLabelBefore}
            </Typography>,
            menuItem,
          ];
        })}
        {showCloseAction && !mobileSheet && (
          <Button
            fullWidth
            onClick={handleClose}
            sx={{
              mt: 0.5,
              mb: 0.8,
              color: "#fff",
              fontWeight: 850,
              textTransform: "none",
              "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
            }}
          >
            Close
          </Button>
        )}
      </Menu>
    </>
  );
};

export const formColumnSx = {
  width: "100%",
  maxWidth: { xs: "100%", sm: 600 },
} as const;

// One semantic rhythm for every form-like surface:
// label -> helper uses `mini`, label/helper -> a visible control box uses
// `control`, unboxed text uses the optical `inlineControl` correction, and a
// completed field -> the next field uses the larger `section` rhythm.
// Keep text, selects, toggles, previews, summaries, and custom controls on
// these values instead of adding per-control margin overrides.
export const FORM_FIELD_RHYTHM = {
  mini: 0.5,
  inlineControl: 1.1,
  control: 2.4,
  section: 3,
  stack: 0.6,
} as const;

export const FIELD_CONTROL_GAP = FORM_FIELD_RHYTHM.control;
export const FIELD_SUPPORTING_TEXT_GAP = FORM_FIELD_RHYTHM.mini;
export const FORM_FIELD_CONTENT_GAP = FORM_FIELD_RHYTHM.control;
export const FORM_FIELD_SECTION_GAP = FORM_FIELD_RHYTHM.section;
export const FORM_FIELD_CIRCLE_CONTROL_SIZE = 36;
export const QUANTITY_STEPPER_CONTROL_SIZE = 44;

export const selectionButtonLayoutSx = {
  ...actionButtonSizingSx,
  boxShadow: "none",
  borderRadius: FORM_CONTROL.radius,
  px: { xs: 1.75, sm: 2.25 },
  textTransform: "none",
} as const;

export const getCharacterToggleButtonSx = (
  selected: boolean,
  generating = false,
) => ({
  ...selectionButtonLayoutSx,
  minWidth: 0,
  py: 1,
  justifyContent: "flex-start",
  gap: 1,
  color: "#fff",
  bgcolor: SELECTION_SURFACE,
  border: selected
    ? "2px solid #76A9E7"
    : "2px solid rgba(255,255,255,0.12)",
  opacity: generating ? 0.68 : 1,
  cursor: generating ? "not-allowed" : "pointer",
  "& .character-toggle-avatar": {
    ml: -0.35,
  },
  "&:hover": {
    bgcolor: generating ? SELECTION_SURFACE : SELECTION_SURFACE_HOVER,
    borderColor: selected ? "#76A9E7" : "rgba(255,255,255,0.18)",
  },
});

export const getReadOnlyCharacterToggleSx = (
  selected: boolean,
  generating = false,
) => {
  const baseSx = getCharacterToggleButtonSx(selected, generating);
  return {
    ...baseSx,
    display: "inline-flex",
    alignItems: "center",
    boxSizing: "border-box",
    width: "fit-content",
    flex: "0 0 auto",
    color: "#fff",
    bgcolor: SELECTION_SURFACE,
    opacity: generating ? 0.68 : 1,
    cursor: "default",
    "&:hover": {
      bgcolor: SELECTION_SURFACE,
      borderColor: selected ? "#76A9E7" : "rgba(255,255,255,0.12)",
    },
  };
};

export const fieldSectionSx = {
  ...formColumnSx,
  py: FORM_FIELD_SECTION_GAP / 2,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
} as const;

export const firstFieldSectionSx = {
  ...fieldSectionSx,
  pt: 0,
} as const;

// Use inside rows that already provide their own leading visual, such as a
// timeline icon. It preserves the bottom half of the section rhythm while
// aligning the field label to the row's visual top edge.
export const embeddedFieldSectionSx = {
  ...fieldSectionSx,
  pt: 0,
} as const;

export const formFieldStackSx = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: `var(--fable-form-stack-gap, ${FORM_FIELD_RHYTHM.stack * 8}px)`,
} as const;

/** Divided reading sections share one equal gap on either side of every rule. */
export const dividedFormFieldStackSx = {
  ...formFieldStackSx,
  alignItems: "stretch",
  gap: 0,
  "& > :not(style)": {
    width: "100%",
    margin: 0,
    py: FORM_FIELD_RHYTHM.section,
  },
  "& > :not(style) + :not(style)": { borderTop: "1px solid rgba(255,255,255,0.14)" },
  "& > :first-child": { pt: 0 },
  "& > :last-child": { pb: 0 },
} as const;

// A non-FormField child of a centered form stack must opt back into the full
// stack width. This prevents scroll anchors and other semantic wrappers from
// shrink-wrapping large controls or media previews.
export const formFieldStackItemSx = {
  width: "100%",
} as const;

export const pageActionSectionSx = {
  pt: (FORM_FIELD_RHYTHM.section / 2 + FORM_FIELD_RHYTHM.stack) * 2,
  pb: (FORM_FIELD_RHYTHM.section / 2 + FORM_FIELD_RHYTHM.stack) * 2,
} as const;

export const formFieldControlSx = {
  width: "100%",
  mt: FORM_FIELD_CONTENT_GAP,
  textAlign: "left",
} as const;

// Unboxed text has no visible border at its control-box edge. This optical
// correction makes its first glyph begin at the same perceived gap as a
// bordered toggle, select, preview, or summary.
export const inlineFormFieldControlSx = {
  ...formFieldControlSx,
  mt: FORM_FIELD_RHYTHM.inlineControl,
} as const;

export const quantityStepperSx = {
  display: "grid",
  gridTemplateColumns: `${QUANTITY_STEPPER_CONTROL_SIZE}px 52px ${QUANTITY_STEPPER_CONTROL_SIZE}px`,
  alignItems: "center",
  justifyContent: "start",
  gap: 1,
} as const;

export const quantityStepperButtonSx = {
  width: QUANTITY_STEPPER_CONTROL_SIZE,
  height: QUANTITY_STEPPER_CONTROL_SIZE,
  minWidth: QUANTITY_STEPPER_CONTROL_SIZE,
  minHeight: QUANTITY_STEPPER_CONTROL_SIZE,
  p: 0,
  borderRadius: "50%",
  color: "#fff",
  bgcolor: "rgba(255,255,255,0.08)",
  flex: `0 0 ${QUANTITY_STEPPER_CONTROL_SIZE}px`,
  "&:hover": {
    bgcolor: "rgba(255,255,255,0.14)",
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.32)",
    bgcolor: "rgba(255,255,255,0.05)",
  },
} as const;

export const quantityStepperValueSx = {
  color: "#fff",
  fontSize: 20,
  fontWeight: 900,
  textAlign: "center",
} as const;

export const helperTextSx = {
  ...productSupportingTextSx,
  mb: FIELD_CONTROL_GAP,
  textAlign: "left",
};

export const tokenCostIconSx = {
  width: 17,
  height: 17,
  flexShrink: 0,
  opacity: 1,
  filter: "none",
};

export const TokenCostInline = ({
  label,
  amount,
  iconSrc = "/coin.png",
}: {
  label: React.ReactNode;
  amount: React.ReactNode;
  iconSrc?: string;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.55}
    component="span"
    sx={{ minWidth: 0 }}
  >
    <Box
      component="span"
      sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
    >
      {label}
    </Box>
    <Box component="span" sx={{ color: "inherit", flexShrink: 0 }}>
      |
    </Box>
    <FableTokenAmount amount={amount} kind="price" iconSrc={iconSrc} />
  </Stack>
);

export const OptionalLabel = ({
  children = "(optional)",
}: {
  children?: React.ReactNode;
}) => (
  <Typography
    component="span"
    sx={{
      color: "rgba(255,255,255,0.62)",
      fontSize: { xs: 15, md: 16 },
      fontWeight: 700,
      lineHeight: 1,
      letterSpacing: 0,
      ml: 0.55,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Typography>
);

export const formFieldLabelContentSx = {
  display: "inline-flex",
  alignItems: "center",
  flexWrap: "wrap",
} as const;

export const primaryActionButtonSx = {
  ...actionButtonSizingSx,
  bgcolor: "#D8A363",
  color: "#0e0c0d",
  borderRadius: "12px",
  letterSpacing: 0,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  textTransform: "none",
  "& .token-cost-icon": tokenCostIconSx,
  "&:hover": { bgcolor: "#f0b45f" },
  "&:disabled": {
    bgcolor: "rgba(216,163,99,0.32)",
    color: "rgba(14,12,13,0.48)",
    "& .token-cost-icon, & .MuiButton-startIcon, & .MuiButton-endIcon": {
      opacity: 0.42,
      filter: "saturate(0.6) brightness(0.76)",
    },
  },
};

export const storyToolActionRowSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "repeat(2, minmax(0, 1fr))",
  },
  alignItems: "center",
  justifyContent: "stretch",
  gap: 1.4,
  width: "100%",
  maxWidth: "100%",
  mt: { xs: 2.6, md: 2.8 },
  pt: { xs: 2.6, md: 2.8 },
  borderTop: "1px solid rgba(255,255,255,0.10)",
  "@media (max-width: 420px)": {
    gridTemplateColumns: "1fr",
  },
};

export const storyToolActionButtonSx = {
  ...compactButtonSizingSx,
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  borderRadius: "12px",
  lineHeight: 1.15,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "& .MuiButton-endIcon": {
    ml: { xs: 0.55, md: 0.75 },
    "& svg": { fontSize: { xs: 18, md: 20 } },
  },
  "& > span:first-of-type": {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .token-cost-icon": {
    width: { xs: 16, md: 17 },
    height: { xs: 16, md: 17 },
  },
};

export const dangerActionButtonSx = {
  ...primaryActionButtonSx,
  bgcolor: "#EF4444",
  "&:hover": { bgcolor: "#ff6262" },
};

export const FableInfoTooltip = ({
  title,
  note,
  buttonLabel,
  infoLabel = "Info",
  noteLabel = "Note",
  placement = "top",
  sx,
  iconSx,
}: {
  title: React.ReactNode;
  note?: React.ReactNode;
  buttonLabel?: string;
  infoLabel?: React.ReactNode;
  noteLabel?: React.ReactNode;
  placement?: "top" | "right" | "bottom" | "left";
  sx?: object;
  iconSx?: object;
}) => {
  const [open, setOpen] = React.useState(false);
  return <FableTooltip
    {...(buttonLabel ? { open, onOpen: () => setOpen(true), onClose: () => setOpen(false) } : {})}
    describeChild={!!buttonLabel}
    title={
      <Box sx={{ width: "100%" }}>
        <Typography
          sx={{
            color: "#D8A363",
            fontSize: 11,
            fontWeight: 800,
            lineHeight: 1.2,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            mb: 0.5,
          }}
        >
          {infoLabel}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.94)",
            fontSize: note ? 14 : 15,
            fontWeight: 600,
            lineHeight: note ? 1.4 : 1.45,
            width: "auto",
            maxWidth: "100%",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        >
          {title}
        </Typography>
        {note && (
          <>
            <Box sx={{ my: 1.1, borderTop: "1px solid rgba(255,255,255,0.16)" }} />
            <Typography
              sx={{
                color: "#D8A363",
                fontSize: 11,
                fontWeight: 800,
                lineHeight: 1.2,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mb: 0.5,
              }}
            >
              {noteLabel}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.4,
                whiteSpace: "normal",
                overflowWrap: "break-word",
              }}
            >
              {note}
            </Typography>
          </>
        )}
      </Box>
    }
    placement={placement}
    enterTouchDelay={0}
    leaveTouchDelay={3500}
  >
    <Box
      component={buttonLabel ? "button" : "span"}
      {...(buttonLabel
        ? { type: "button" as const, "aria-label": buttonLabel }
        : {})}
      onClick={(event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (buttonLabel) setOpen(true);
      }}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: { xs: "25.3px", md: "27.6px" },
        flexShrink: 0,
        cursor: "pointer",
        ...(buttonLabel
          ? { border: 0, p: 0, bgcolor: "transparent", borderRadius: "50%" }
          : {}),
        ...sx,
      }}
    >
      <InfoIcon
        sx={{
          color: "#B3B3B3",
          fontSize: { xs: 22, md: 23 },
          display: "block",
          ...iconSx,
        }}
      />
    </Box>
  </FableTooltip>;
};

export const SectionTitle = ({
  children,
  info,
  infoNote,
  mb = FIELD_CONTROL_GAP,
  right,
  titleAction,
  onTitleClick,
  stackRightOnMobile = false,
  rightReserve = 7,
  labelSx,
}: {
  children: React.ReactNode;
  info?: string;
  infoNote?: string;
  mb?: number;
  right?: React.ReactNode;
  titleAction?: React.ReactNode;
  onTitleClick?: () => void;
  stackRightOnMobile?: boolean;
  rightReserve?: number | string;
  labelSx?: object;
}) => {
  const trailingAction =
    right && titleAction ? (
      <Stack direction="row" spacing={1} alignItems="center">
        {titleAction}
        {right}
      </Stack>
    ) : (
      right || titleAction
    );

  return (
    <Stack
      direction={{ xs: stackRightOnMobile ? "column" : "row", sm: "row" }}
      alignItems={{
        xs: stackRightOnMobile ? "stretch" : "center",
        sm: "center",
      }}
      justifyContent="space-between"
      sx={{
        mb,
        width: "100%",
        position: "relative",
        gap: stackRightOnMobile ? { xs: 1, sm: 0 } : 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.1}
        sx={{
          minWidth: 0,
          width: "100%",
          boxSizing: "border-box",
          pr: trailingAction
            ? stackRightOnMobile
              ? { xs: 0, sm: rightReserve }
              : rightReserve
            : 0,
          flexWrap: "wrap",
          rowGap: 0.8,
        }}
      >
        {onTitleClick ? (
          <Button
            type="button"
            onClick={onTitleClick}
            className="fable-form-field-label"
            sx={{
              minWidth: 0,
              p: 0,
              ...productSectionTitleSx,
              textTransform: "none",
              justifyContent: "flex-start",
              "&:hover": { bgcolor: "transparent", color: "#fff" },
              ...labelSx,
            }}
          >
            {children}
          </Button>
        ) : (
          <Typography
            className="fable-form-field-label"
            sx={{
              ...productSectionTitleSx,
              ...labelSx,
            }}
          >
            {children}
          </Typography>
        )}
        {info && <FableInfoTooltip title={info} note={infoNote} />}
      </Stack>
      {trailingAction && (
        <Box
          sx={
            stackRightOnMobile
              ? {
                  alignSelf: { xs: "flex-end", sm: "auto" },
                  position: { xs: "static", sm: "absolute" },
                  right: { sm: 0 },
                  top: { sm: "50%" },
                  transform: { sm: "translateY(-50%)" },
                }
              : {
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                }
          }
        >
          {trailingAction}
        </Box>
      )}
    </Stack>
  );
};

type FormSectionProps = {
  title: React.ReactNode;
  titleSuffix?: React.ReactNode;
  subtitle?: React.ReactNode;
  tooltipText?: string;
  tooltipNoteText?: string;
  leading?: React.ReactNode;
  headerAction?: React.ReactNode;
  children?: React.ReactNode;
  supportingContent?: React.ReactNode;
  action?: React.ReactNode;
  dividerAfter?: boolean;
  isLast?: boolean;
  required?: boolean;
  optional?: boolean;
  titleAction?: React.ReactNode;
  onTitleClick?: () => void;
  stackRightOnMobile?: boolean;
  rightReserve?: number | string;
  sectionSx?: object;
  subtitleSx?: object;
  controlSx?: object;
  supportingContentSx?: object;
  controlPresentation?: "block" | "inline";
  labelSx?: object;
  labelVisuallyHidden?: boolean;
};

/**
 * Canonical vertical form block shared with the App contract:
 * title -> optional subtitle -> control -> optional supporting/action content.
 * `FormField` below remains as a backwards-compatible adapter.
 */
export const FormSection = ({
  title,
  titleSuffix,
  subtitle,
  tooltipText,
  tooltipNoteText,
  leading,
  headerAction,
  children,
  supportingContent,
  action,
  dividerAfter = false,
  isLast = false,
  required = false,
  optional = false,
  titleAction,
  onTitleClick,
  stackRightOnMobile = false,
  rightReserve = 7,
  sectionSx,
  subtitleSx,
  controlSx,
  supportingContentSx,
  controlPresentation = "block",
  labelSx,
  labelVisuallyHidden = false,
}: FormSectionProps) => {
  const hasSubtitle =
    subtitle !== undefined && subtitle !== null && subtitle !== false;
  const hasControl =
    children !== undefined && children !== null && children !== false;

  return (
    <Box
      className="fable-form-field"
      sx={{
        ...fieldSectionSx,
        ...(isLast ? { pb: 0 } : {}),
        ...sectionSx,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
        {leading && <Box sx={{ flexShrink: 0 }}>{leading}</Box>}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SectionTitle
            info={tooltipText}
            infoNote={tooltipNoteText}
            mb={hasSubtitle ? FIELD_SUPPORTING_TEXT_GAP : 0}
            right={headerAction}
            titleAction={titleAction}
            onTitleClick={onTitleClick}
            stackRightOnMobile={stackRightOnMobile}
            rightReserve={rightReserve}
            labelSx={
              labelVisuallyHidden ? { ...labelSx, ...visuallyHidden } : labelSx
            }
          >
            <Box component="span" sx={formFieldLabelContentSx}>
              {title}
              {titleSuffix && (
                <Box component="span" sx={{ ml: 0.7 }}>
                  {titleSuffix}
                </Box>
              )}
              {required && (
                <Box component="span" sx={{ color: "#FF4444", ml: 0.5 }}>
                  *
                </Box>
              )}
              {optional && <OptionalLabel />}
            </Box>
          </SectionTitle>
          {hasSubtitle && (
            <Typography className="fable-form-field-description" sx={{ ...helperTextSx, mb: 0, ...subtitleSx }}>
              {subtitle}
            </Typography>
          )}
          {hasControl && (
            <Box
              className="fable-form-field-control"
              sx={{
                ...(controlPresentation === "inline"
                  ? inlineFormFieldControlSx
                  : formFieldControlSx),
                // A screen-reader-only label does not need a visible control gap.
                ...(labelVisuallyHidden && !hasSubtitle ? { mt: 0 } : {}),
                ...controlSx,
              }}
            >
              {children}
            </Box>
          )}
          {supportingContent && (
            <Box
              sx={{
                mt: FORM_FIELD_RHYTHM.control,
                width: "100%",
                ...supportingContentSx,
              }}
            >
              {supportingContent}
            </Box>
          )}
          {action && (
            <Box sx={{ mt: FORM_FIELD_RHYTHM.control, width: "100%" }}>
              {action}
            </Box>
          )}
        </Box>
      </Stack>
      {dividerAfter && (
        <Box
          aria-hidden="true"
          sx={{
            width: "100%",
            mt: FORM_FIELD_RHYTHM.section,
            borderBottom: "1px solid rgba(255,255,255,0.14)",
          }}
        />
      )}
    </Box>
  );
};

export const FormField = ({
  label,
  info,
  infoNote,
  description,
  right,
  descriptionSx,
  ...props
}: Omit<FormSectionProps, "title" | "subtitle" | "tooltipText" | "headerAction"> & {
  label: React.ReactNode;
  info?: string;
  infoNote?: string;
  description?: React.ReactNode;
  right?: React.ReactNode;
  descriptionSx?: object;
}) => (
  <SelectLabelContext.Provider value={label}>
  <FormSection
    {...props}
    title={label}
    subtitle={description}
    tooltipText={info}
    tooltipNoteText={infoNote}
    headerAction={right}
    subtitleSx={descriptionSx}
  />
  </SelectLabelContext.Provider>
);

export const ToggleGroup = ({
  value,
  options,
  onChange,
  disabled = false,
  wrap = false,
}: {
  value: string;
  options: ToggleOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  wrap?: boolean;
}) => {
  const optionRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});
  const selectedOptionToCenterRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const selectedValue = selectedOptionToCenterRef.current;
    if (!selectedValue || selectedValue !== value) return;

    const frame = requestAnimationFrame(() => {
      optionRefs.current[selectedValue]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
      selectedOptionToCenterRef.current = null;
    });

    return () => cancelAnimationFrame(frame);
  }, [value]);

  const buttons = options.map((option) => {
    const selected = value === option.value;
    const selectedGradient = option.highlight && selected;
    const gradientType = option.gradientType || "orange-purple";
    return (
      <Button
        className="fable-selection-button"
        key={option.value}
        aria-pressed={selected}
        ref={(node) => {
          optionRefs.current[option.value] = node;
        }}
        disabled={disabled || option.disabled}
        onClick={() => {
          selectedOptionToCenterRef.current = wrap ? null : option.value;
          onChange(option.value);
        }}
        sx={{
          ...selectionButtonLayoutSx,
          color:
            disabled || option.disabled ? "rgba(255,255,255,0.36)" : "#fff",
          background: selectedGradient
            ? `linear-gradient(${FORM_CONTROL.surface}, ${FORM_CONTROL.surface}) padding-box, ${TOGGLE_GRADIENTS[gradientType]} border-box`
            : FORM_CONTROL.surface,
          border: selectedGradient
            ? "2px solid transparent"
            : `2px solid ${selected ? FORM_CONTROL.focus : FORM_CONTROL.border}`,
          cursor: disabled || option.disabled ? "not-allowed" : "pointer",
          "&:hover": {
            background: selectedGradient
              ? `linear-gradient(${FORM_CONTROL.hoverSurface}, ${FORM_CONTROL.hoverSurface}) padding-box, ${TOGGLE_GRADIENTS[gradientType]} border-box`
              : FORM_CONTROL.hoverSurface,
          },
          "&.Mui-disabled": {
            color: "rgba(255,255,255,0.36)",
            background: "rgba(255,255,255,0.08)",
            borderColor: "rgba(255,255,255,0.12)",
            opacity: 1,
            pointerEvents: "auto",
          },
        }}
      >
        <Stack
          direction="row"
          spacing={0.65}
          alignItems="center"
          component="span"
        >
          <Box component="span">{option.label}</Box>
          {option.rightText && (
            <Box
              component="span"
              sx={{
                whiteSpace: "nowrap",
                opacity: 1,
              }}
            >
              | {option.rightImage === "/coin.png"
                ? <FableTokenAmount amount={option.rightText} kind="price" />
                : option.rightText}
            </Box>
          )}
          {option.rightImage && !(option.rightText && option.rightImage === "/coin.png") && (
            <Box
              component="img"
              src={option.rightImage}
              alt=""
              sx={tokenCostIconSx}
            />
          )}
        </Stack>
      </Button>
    );
  });
  if (wrap) return <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.4, width: "100%" }}>{buttons}</Box>;

  return (
    <HorizontalOverflowScroller
      ariaLabel="Options"
      fadeColor="#101219"
      fadeWidth={40}
      sx={{
        width: "100%",
        maxWidth: "100%",
      }}
      contentSx={{
        gap: 1.4,
        pb: 0.35,
        "& .MuiButton-root": { flex: "0 0 auto" },
      }}
    >
      {buttons}
    </HorizontalOverflowScroller>
  );
};

export const QuantityStepper = ({
  value,
  onDecrease,
  onIncrease,
  decreaseDisabled = false,
  increaseDisabled = false,
  decreaseLabel = "Decrease quantity",
  increaseLabel = "Increase quantity",
  controlSize = QUANTITY_STEPPER_CONTROL_SIZE,
}: {
  value: React.ReactNode;
  onDecrease: () => void;
  onIncrease: () => void;
  decreaseDisabled?: boolean;
  increaseDisabled?: boolean;
  decreaseLabel?: string;
  increaseLabel?: string;
  controlSize?: number;
}) => (
  <Box
    sx={{
      ...quantityStepperSx,
      gridTemplateColumns: `${controlSize}px 52px ${controlSize}px`,
    }}
  >
    <IconButton
      aria-label={decreaseLabel}
      onClick={onDecrease}
      disabled={decreaseDisabled}
      sx={{
        ...quantityStepperButtonSx,
        width: controlSize,
        height: controlSize,
        minWidth: controlSize,
        minHeight: controlSize,
        flex: `0 0 ${controlSize}px`,
      }}
    >
      <RemoveIcon sx={{ fontSize: 24 }} />
    </IconButton>
    <Typography sx={quantityStepperValueSx}>{value}</Typography>
    <IconButton
      aria-label={increaseLabel}
      onClick={onIncrease}
      disabled={increaseDisabled}
      sx={{
        ...quantityStepperButtonSx,
        width: controlSize,
        height: controlSize,
        minWidth: controlSize,
        minHeight: controlSize,
        flex: `0 0 ${controlSize}px`,
      }}
    >
      <AddIcon sx={{ fontSize: 24 }} />
    </IconButton>
  </Box>
);

export const getSelectLabel = (option?: DetailOption) =>
  option?.label || option?.value || "";

const selectedOptionIndicatorSx = {
  bgcolor: "#76A9E7",
  borderColor: "#76A9E7",
  "& svg": { opacity: 1 },
} as const;

/** The row owns selection and keyboard handling; this is its visual indicator. */
export const OptionSelectionIndicator = ({ selected }: { selected?: boolean }) => (
  <Box
    component="span"
    aria-hidden="true"
    className="fable-option-selection"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 22,
      height: 22,
      boxSizing: "border-box",
      flexShrink: 0,
      border: "1px solid #757575",
      borderRadius: "50%",
      color: "#000",
      "& svg": { fontSize: 16, opacity: 0 },
      ...(selected ? selectedOptionIndicatorSx : {}),
      ...(selected === undefined
        ? { ".Mui-selected &": selectedOptionIndicatorSx }
        : {}),
    }}
  >
    <CheckIcon />
  </Box>
);

/** Custom dropdown content uses the same trailing selection indicator as standard options. */
export const DropdownSelectionRow = ({ children }: { children: React.ReactNode }) => (
  <Stack direction="row" alignItems="center" spacing={1.4} sx={{ width: "100%", minWidth: 0 }}>
    <Box sx={{ flex: 1, minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}>
      {children}
    </Box>
    <OptionSelectionIndicator />
  </Stack>
);

export const DropdownOption = ({
  option,
  hideUnlock = false,
  inInput = false,
  showSelectionIndicator = true,
  selected,
}: {
  option: DetailOption;
  hideUnlock?: boolean;
  inInput?: boolean;
  showSelectionIndicator?: boolean;
  selected?: boolean;
}) => {
  const visualSize = inInput ? FORM_CONTROL.dropdownVisualSize : 44;
  const avatarSize = typeof option.progress === "number"
    ? visualSize - 8
    : visualSize;
  if (option.isSection) {
    return (
      <Typography
        sx={{
          width: "100%",
          color: "rgba(255,255,255,0.58)",
          WebkitTextFillColor: "rgba(255,255,255,0.58)",
          fontWeight: 900,
          fontSize: { xs: 14, md: 15 },
          letterSpacing: 0,
          textAlign: "left",
          pt: 0.7,
          pb: 0.2,
        }}
      >
        {getSelectLabel(option)}
      </Typography>
    );
  }

  const unlockLabel = hideUnlock
    ? ""
    : option.unlockLabel || (option.locked ? "Unlock Feature 👑" : "");
  const trailingLabel = hideUnlock ? "" : option.statusLabel || unlockLabel;
  const trailingColor = option.statusLabel ? "#76A9E7" : "#D8A363";
  const isMuted = Boolean(option.disabled && !option.locked);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.4}
      sx={{
        minWidth: 0,
        width: "100%",
        opacity: isMuted ? 0.58 : 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.4}
        sx={{ minWidth: 0, flex: 1 }}
      >
        {option.isAction ? (
          <Box
            sx={{
              ...circularControlVisualSx(visualSize),
              bgcolor: "#D8A363",
              color: "#111318",
              display: "grid",
              placeItems: "center",
            }}
          >
            <AddIcon sx={{ fontSize: 27 }} />
          </Box>
        ) : (
          option.image && (
            <Box
              sx={{
                position: "relative",
                ...circularControlVisualSx(visualSize),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {typeof option.progress === "number" && (
                <GenerationProgress progress={option.progress} size={visualSize} />
              )}
              <Avatar
                src={option.image}
                sx={{
                  ...circularControlVisualSx(avatarSize),
                  ...dropdownVisualBorderSx,
                  "--fable-visual-border-color": "rgba(255,255,255,0.24)",
                  bgcolor: "rgba(255,255,255,0.08)",
                  filter:
                    typeof option.progress === "number"
                      ? "brightness(0.84)"
                      : "none",
                }}
              />
            </Box>
          )
        )}
        <Stack spacing={inInput ? 0 : 0.2} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: FORM_CONTROL.fontSize,
              lineHeight: inInput ? 1.25 : undefined,
              color: option.isAction
                ? "rgba(255,255,255,0.62)"
                : option.defaultLabel
                  ? "rgba(255,255,255,0.70)"
                  : "#fff",
              WebkitTextFillColor: option.isAction
                ? "rgba(255,255,255,0.62)"
                : option.defaultLabel
                  ? "rgba(255,255,255,0.70)"
                  : "#fff",
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getSelectLabel(option)}
            {option.defaultLabel ? " (default)" : ""}
          </Typography>
          {option.subtitle && (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.56)",
                WebkitTextFillColor: "rgba(255,255,255,0.56)",
                fontWeight: 750,
                fontSize: inInput ? 12 : { xs: 12.5, md: 13 },
                lineHeight: inInput ? 1 : 1.12,
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {option.subtitle}
            </Typography>
          )}
        </Stack>
      </Stack>
      {trailingLabel && (
        <Typography
          sx={{
            color: trailingColor,
            WebkitTextFillColor: trailingColor,
            fontWeight: 900,
            fontSize: { xs: 15, md: 16 },
            whiteSpace: "nowrap",
            ml: 1.5,
            flexShrink: 0,
          }}
        >
          {trailingLabel}
        </Typography>
      )}
      {!inInput && showSelectionIndicator && !option.isAction && !option.disabled && (
        <OptionSelectionIndicator selected={selected} />
      )}
    </Stack>
  );
};

export const renderSelectValue = (
  selected: unknown,
  options: DetailOption[],
) => {
  const selectedValue = String(selected || "");
  const option = options.find((item) => item.value === selectedValue);
  if (!selectedValue) {
    return options[0] ? (
      <DropdownOption option={options[0]} hideUnlock inInput />
    ) : null;
  }
  if (!option) {
    return (
      <Typography
        sx={{
          color: "#fff",
          WebkitTextFillColor: "#fff",
          fontWeight: 850,
          textAlign: "left",
        }}
      >
        {selectedValue}
      </Typography>
    );
  }
  return <DropdownOption option={option} hideUnlock inInput />;
};

export const DetailSelect = ({
  value,
  ariaLabel,
  options,
  onChange,
  onOpen,
  disabled = false,
}: {
  value: string;
  ariaLabel?: string;
  options: DetailOption[];
  onChange: (value: string) => void;
  onOpen?: () => void;
  disabled?: boolean;
}) => (
  <FormControl
    variant="outlined"
    sx={{
      alignSelf: "flex-start",
      width: "100%",
      maxWidth: "100%",
      ml: 0,
      mr: "auto",
      "& .MuiInputBase-root": {
        width: "100%",
      },
    }}
  >
    <Select
      inputProps={ariaLabel ? { "aria-label": ariaLabel } : undefined}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(String(event.target.value))}
      onOpen={onOpen}
      variant="outlined"
      sx={selectSx}
      renderValue={(selected) => renderSelectValue(selected, options)}
      MenuProps={menuProps}
    >
      {options.map((option) => option.isSection ? (
        <ListSubheader key={option.value} disableSticky sx={dropdownSectionLabelSx}>
          {option.label || option.value}
        </ListSubheader>
      ) : (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          sx={{
            "&.Mui-disabled": {
              opacity: 1,
            },
          }}
        >
          <DropdownOption option={option} />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);
